import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, TextField, Typography, Grid } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { EmployeeAPIUrl, DownloadDocumentAPIUrl } from '../utils/extensions/functionEx';
import { HomeContext } from '../utils/contexts/homeContext';
import { EmployeeFormContext } from '../utils/contexts/employeeFormContext';
import { EmployeeStatus, FormMode } from '../utils/extensions/constantEx';
import { GetDayJSValueFromUTCDate, GetDateFromDayJSValue, GetNoToCurrencyFormat } from '../utils/extensions/functionEx';
import PageLoading from '../shared/components/backdrop';
import EmployeeFormHeader from './employeeFormHeader';
import EmployeePersonalInfo from './employeePersonalInfo'
import EmployeeDocumentView from './employeeDocs';
import ProfilePhotoCard from '../shared/components/profilePhoto';
import DocumentForm from '../shared/components/documentForm';
import ConfirmDialog from '../shared/components/confirmDialog';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';

const EmployeeForm = (props) => {
    const { formMode, selectedEmployeeId } = props;
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { employeeFormValue, removedDocIds, selectedEmployeeJson,
        SetRemovedEmpDocIds, SetRemovedDocIds, SetEmployeeFormValue, SetSelectedEmployeeJson, NewEmployeeFormInitalState } = useContext(EmployeeFormContext);

    const location = useLocation();
    const navigate = useNavigate();

    const [isOpenPageLoad, setIsOpenPageLoad] = useState(false);
    const [formOpenMode, setFormOpenMode] = useState(formMode ?? FormMode.NewMode);
    const [cleared, setCleared] = useState(false);
    const [isShowValidationMsgs, setIsShowValidationMsgs] = useState(false);
    const [employeeName, setEmployeeName] = useState('');
    const [statusText, setStatusText] = useState('');
    const [mobileNo, setMobileNo] = useState('');
    const [phone1, setPhone1] = useState('');
    const [hireDate, setHireDate] = useState(null);
    const [isOpenEditProfilePhoto, setIsOpenEditProfilePhoto] = React.useState(false);
    const [isOpenProfilePhotoDeleteConfirm, setiIsOpenProfilePhotoDeleteConfirm] = React.useState(false);
    const [profilePhotoFile, setProfilePhotoFile] = React.useState(null);
    const [profilePhotoUrl, setProfilePhotoUrl] = React.useState(null);
    const [isOpenEmpDeleteConfirm, setIsOpenEmpDeleteConfirm] = React.useState(false);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        setFormMode(formMode ?? FormMode.NewMode);
        if (selectedEmployeeId > 0) {
            getEmployeeFromDB(selectedEmployeeId);
        }
    }, [selectedEmployeeId, formMode]);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    const handlePageLoadingOpen = () => {
        setIsOpenPageLoad(true);
    }

    const handlePageLoadingClose = () => {
        setIsOpenPageLoad(false);
    }

    const handleEditClick = () => {
        if (formOpenMode === FormMode.ViewMode && selectedEmployeeJson && selectedEmployeeJson.id && selectedEmployeeJson.id > 0) {
            setFormMode(FormMode.EditMode);
            getEmployeeFromDB(selectedEmployeeJson.id);
        }
    }

    const handleSaveClick = async () => {
        try {
            if (!isFormValid()) {
                setIsShowValidationMsgs(true);
                return;
            }
            setIsOpenPageLoad(true);
            let tempEmpId = 0;

            if (formOpenMode === FormMode.NewMode) {
                tempEmpId = await addNewEmployeeAPI();
            } else if (formOpenMode === FormMode.EditMode) {
                tempEmpId = await updateEmployeeAPI();
            }

            if (tempEmpId > 0) {
                setFormMode(FormMode.ViewMode);
                await getEmployeeFromDB(tempEmpId);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
        finally {
            handlePageLoadingClose();
        }
    }

    const handleCancelClick = async () => {
        if (formOpenMode === FormMode.ViewMode || formOpenMode === FormMode.NewMode) {
            navigate("/search-employees", {
                replace: false
            });
            return;
        }

        setFormMode(FormMode.ViewMode);
        if (selectedEmployeeId > 0) {
            await getEmployeeFromDB(selectedEmployeeId);
        }
    }

    const handleOpenProfilePhotoEditClick = () => {
        setIsOpenEditProfilePhoto(true);
    }

    const handleOpenProfilePhotoDeleteClick = () => {
        setiIsOpenProfilePhotoDeleteConfirm(true);
    }

    const handleProfilePhotoSaveClick = (profileDocument) => {
        if (profilePhotoFile) {
            SetRemovedDocIds([...removedDocIds, profilePhotoFile.uploadedFileId]);
        }
        setProfilePhotoFile(profileDocument);
        setProfilePhotoUrl(null);
        setIsOpenEditProfilePhoto(false);
    }

    const handleProfilePhotoCancelClick = () => {
        setIsOpenEditProfilePhoto(false);
    }

    const handleProfilePhotoDeleteConfirmOKClick = () => {
        if (profilePhotoFile) {
            SetRemovedDocIds([...removedDocIds, profilePhotoFile.uploadedFileId]);
        }
        setProfilePhotoFile(null);
        setProfilePhotoUrl(null);
        setiIsOpenProfilePhotoDeleteConfirm(false);
    }

    const handleProfilePhotoDeleteConfirmCancelClick = () => {
        setiIsOpenProfilePhotoDeleteConfirm(false);
    }

    const clearForm = () => {
        setCleared('');
        setEmployeeName('');
        setMobileNo('');
        setPhone1('');
        setProfilePhotoFile(null);
        setProfilePhotoUrl(null);
        SetSelectedEmployeeJson(null);
        SetRemovedEmpDocIds([]);
        SetRemovedDocIds([]);
        SetEmployeeFormValue(null);
        setIsShowValidationMsgs(false);
        setStatusText('')
    }

    const setFormMode = (tempFrmMode) => {
        clearForm();
        setFormOpenMode(tempFrmMode);
        SetSelectedEmployeeJson({ ...NewEmployeeFormInitalState });
        SetEmployeeFormValue({ ...NewEmployeeFormInitalState });
    }

    const isFormValid = () => {
        const tempEmployee = { ...employeeFormValue };

        if (employeeName.trim().length === 0 || mobileNo.trim().length === 0 || !hireDate || !tempEmployee.dob || parseInt(tempEmployee.genderId ?? 0) < 0
            || parseInt(tempEmployee.religionId ?? 0) <= 0
            || !tempEmployee.address1 || !tempEmployee.address1.address || tempEmployee.address1.address.trim().length === 0
            || !tempEmployee.address1.district || tempEmployee.address1.district.trim().length === 0) {
            return false;
        }
        return true;
    }

    const getEmployeeRequestFormValues = () => {
        const tempEmployee = { ...employeeFormValue };
        const requestFormValue = {
            id: 0,
            code: '',
            photo_id: 0
        };
        requestFormValue.employee_name = employeeName.trim();
        requestFormValue.phone1 = mobileNo.trim();
        requestFormValue.phone2 = phone1.trim();
        requestFormValue.hire_date = GetDateFromDayJSValue(hireDate);
        requestFormValue.dob = GetDateFromDayJSValue(tempEmployee.dob);
        requestFormValue.gender_id = tempEmployee.genderId;
        requestFormValue.spouse_name = tempEmployee.spouseName.trim();
        requestFormValue.father_name = tempEmployee.fatherName.trim();
        requestFormValue.mother_name = tempEmployee.motherName.trim();
        requestFormValue.address1 = tempEmployee.address1;
        requestFormValue.religion_id = tempEmployee.religionId;
        requestFormValue.notes = tempEmployee.notes.trim();

        if (profilePhotoFile && profilePhotoFile.uploadedFileId && profilePhotoFile.uploadedFileId > 0) {
            requestFormValue.photo_id = profilePhotoFile.uploadedFileId;
        }
        return { ...requestFormValue };
    }

    const addNewEmployeeAPI = async () => {
        try {
            const tempFromValue = getEmployeeRequestFormValues();
            tempFromValue.id = 0;

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(EmployeeAPIUrl(homeState.loggedUser.companyCode), {
                method: "POST",
                headers: headers,
                body: JSON.stringify(tempFromValue)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 201) {
                OpenSuccessAlert('New employee added successfully.');

                const tempJsonEmp = await response.json();
                return tempJsonEmp.id;
            }
            ShowAPIError(response);
            return -1;
        }
        catch (error) {
            OpenErrorAlert(error)
            return -1;
        }
    }

    const updateEmployeeAPI = async () => {
        try {
            const tempFromValue = getEmployeeRequestFormValues();
            tempFromValue.id = selectedEmployeeJson.id;

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(EmployeeAPIUrl(homeState.loggedUser.companyCode), {
                method: "PUT",
                headers: headers,
                body: JSON.stringify(tempFromValue)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Employee details updated successfully.');

                const tempJsonEmp = await response.json();
                return tempJsonEmp.id;
            }
            ShowAPIError(response);
            return -1;
        }
        catch (error) {
            OpenErrorAlert(error)
            return -1;
        }
    }

    const getEmployeeFromDB = async (employeeId) => {
        try {
            let tempDbEmpJson = null;
            setIsOpenPageLoad(true);

            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(EmployeeAPIUrl(homeState.loggedUser.companyCode, employeeId), { headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                tempDbEmpJson = await response.json();
            } else {
                clearForm();
                ShowAPIError(response);
            }

            if (tempDbEmpJson) {
                const tempEmployee = {
                    id: tempDbEmpJson.id,
                    code: tempDbEmpJson.code,
                    dob: GetDayJSValueFromUTCDate(tempDbEmpJson.dob),
                    genderId: tempDbEmpJson.gender_id,
                    spouseName: tempDbEmpJson.spouse_name,
                    fatherName: tempDbEmpJson.father_name,
                    motherName: tempDbEmpJson.mother_name,
                    address1: tempDbEmpJson.address1,
                    religionId: tempDbEmpJson.religion_id,
                    notes: tempDbEmpJson.notes
                };
                SetSelectedEmployeeJson({ ...tempDbEmpJson });
                SetEmployeeFormValue({ ...tempEmployee });

                setEmployeeName(tempDbEmpJson.employee_name);
                setMobileNo(tempDbEmpJson.phone1);
                setPhone1(tempDbEmpJson.phone2);
                setHireDate(GetDayJSValueFromUTCDate(tempDbEmpJson.hire_date));

                setStatusText(tempDbEmpJson.status_id === EmployeeStatus.Active ? 'Active' : 'Terminated');

                await getProfilePhotoFromDB(tempDbEmpJson.photo_id);
            }
        }
        catch (error) {
            clearForm();
            OpenErrorAlert(error);
        } finally {
            handlePageLoadingClose();
        }
    }

    const getProfilePhotoFromDB = async (db_photo_id) => {
        if (db_photo_id && db_photo_id > 0) {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(DownloadDocumentAPIUrl(homeState.loggedUser.companyCode, db_photo_id), { headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const blob = await response.blob();
                const imageObjectUrl = URL.createObjectURL(blob);
                setProfilePhotoFile({ uploadedFileId: db_photo_id });
                setProfilePhotoUrl(imageObjectUrl);
            } else {
                ShowAPIError(response);
            }
        }
    }

    const handleEmployeeDeleteClick = () => {
        if (formOpenMode === FormMode.ViewMode && selectedEmployeeJson && selectedEmployeeJson.id && selectedEmployeeJson.id > 0) {
            setIsOpenEmpDeleteConfirm(true);
        }
    }

    const handleEmpDeleteConfirmOKClick = async () => {
        try {
            handlePageLoadingOpen();
            setIsOpenEmpDeleteConfirm(false);

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const tempFromValue = { id: selectedEmployeeJson.id };
            const response = await fetch(EmployeeAPIUrl(homeState.loggedUser.companyCode), {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify(tempFromValue)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Employee deleted successfully.');
                navigate("/search-employees", {
                    replace: false
                });
            }
            ShowAPIError(response);
        }
        catch (error) {
            OpenErrorAlert(error)
        }
        finally {
            handlePageLoadingClose();
        }
    }

    const handleEmpDeleteConfirmCancelClick = () => {
        setIsOpenEmpDeleteConfirm(false);
    }

    return (
        <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>

                <Stack spacing={0}>
                    {
                        isOpenPageLoad &&
                        <PageLoading isOpenPageLoading={isOpenPageLoad} handleClose={handlePageLoadingClose} />
                    }
                    <EmployeeFormHeader formMode={formOpenMode} handleEditClick={handleEditClick}
                        handleSaveClick={handleSaveClick} handleDeleteClick={handleEmployeeDeleteClick}
                        handleCancelClick={handleCancelClick} />

                    <Box sx={{ padding: "16px", maxHeight: '80vh', overflowY: 'auto' }}>
                        <Stack>
                            <Box>
                                <Grid container spacing={1}>
                                    <Grid size={8}>
                                        <Grid container spacing={2}>
                                            <Grid size={3}>
                                                <TextField size="small" fullWidth disabled={formOpenMode !== FormMode.ViewMode}
                                                    label="Code"
                                                    helperText=""
                                                    value={selectedEmployeeJson?.code || ''}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: true,
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={9}>
                                                <TextField size="small" fullWidth required
                                                    label="Name"
                                                    error={isShowValidationMsgs && employeeName.trim().length === 0 ? true : false}
                                                    helperText={isShowValidationMsgs && employeeName.trim().length === 0 ? 'Enter employee name' : ''}
                                                    value={employeeName}
                                                    onChange={(event) => {
                                                        setEmployeeName(event.target.value);
                                                        setIsShowValidationMsgs(false);
                                                    }}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <TextField size="small" fullWidth required
                                                    label="Mobile No"
                                                    error={isShowValidationMsgs && mobileNo.trim().length === 0 ? true : false}
                                                    helperText={isShowValidationMsgs && mobileNo.trim().length === 0 ? 'Enter mobile no' : ''}
                                                    value={mobileNo}
                                                    onChange={(event) => {
                                                        setMobileNo(event.target.value);
                                                        setIsShowValidationMsgs(false);
                                                    }}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <TextField size="small" fullWidth
                                                    label="Phone 1"
                                                    value={phone1}
                                                    onChange={(event) =>
                                                        setPhone1(event.target.value)
                                                    }
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <DatePicker readOnly={formOpenMode === FormMode.ViewMode} format="DD/MM/YYYY"
                                                    label="Hire Date" maxDate={dayjs()} minDate={dayjs().add(-1, 'year')}
                                                    value={hireDate}
                                                    onChange={(newValue) => { setHireDate(newValue); }}
                                                    slotProps={{
                                                        textField: {
                                                            size: "small", fullWidth: true, required: true,
                                                            error: isShowValidationMsgs && !hireDate ? true : false,
                                                            helperText: isShowValidationMsgs && !hireDate ? 'Enter Hire Date' : 'DD/MM/YYYY'
                                                        },
                                                        field: {
                                                            clearable: true,
                                                            onClear: () => setCleared(true)
                                                        }
                                                    }}
                                                />
                                            </Grid>

                                        </Grid>
                                    </Grid>
                                    <Grid size={1}>
                                        {
                                            isOpenEditProfilePhoto &&
                                            <DocumentForm isOpen={isOpenEditProfilePhoto} isProfilePhoto={true} loggedUser={homeState.loggedUser}
                                                handleSaveClick={handleProfilePhotoSaveClick}
                                                handleCancelClick={handleProfilePhotoCancelClick}
                                                showAPIError={ShowAPIError} openErrorAlert={OpenErrorAlert} />
                                        }
                                        {
                                            isOpenProfilePhotoDeleteConfirm &&
                                            <ConfirmDialog isOpen={isOpenProfilePhotoDeleteConfirm} title="Confirm Delete"
                                                message="Are you sure you want to delete the profile photo?"
                                                handleClickOK={handleProfilePhotoDeleteConfirmOKClick}
                                                handleClickCancel={handleProfilePhotoDeleteConfirmCancelClick} />
                                        }
                                        {
                                            isOpenEmpDeleteConfirm && <ConfirmDialog isOpen={isOpenEmpDeleteConfirm} title="Confirm Delete"
                                                message="Are you sure you want to delete the employee?"
                                                handleClickOK={handleEmpDeleteConfirmOKClick}
                                                handleClickCancel={handleEmpDeleteConfirmCancelClick} />
                                        }
                                    </Grid>
                                    <Grid size={3}>

                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            sx={{
                                                justifyContent: "flex-end",
                                                alignItems: "center",
                                            }}
                                        >

                                            <Typography variant="h4" color='info'>
                                                {statusText}
                                            </Typography>
                                            <ProfilePhotoCard formMode={formOpenMode}
                                                profilePhotoFile={profilePhotoFile} profilePhotoUrl={profilePhotoUrl}
                                                handleEditClick={handleOpenProfilePhotoEditClick}
                                                handleDeleteClick={handleOpenProfilePhotoDeleteClick} />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Grid container>
                                <Grid size={12}>
                                    <Box sx={{ padding: "16px 0" }}>

                                        <EmployeePersonalInfo isShowValidationMsgs={isShowValidationMsgs} formOpenMode={formOpenMode} />

                                        {
                                            formOpenMode === FormMode.ViewMode &&
                                            <EmployeeDocumentView formOpenMode={formOpenMode} selectedEmployeeId={selectedEmployeeId}
                                                handlePageLoadingOpen={handlePageLoadingOpen} handlePageLoadingClose={handlePageLoadingClose} />
                                        }
                                    </Box>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Box>
                </Stack>
            </LocalizationProvider>
        </Box>
    )
}

export default EmployeeForm;